import algoliasearch from 'algoliasearch';
import { createInMemoryCache } from '@algolia/cache-in-memory';
import Fuse from 'fuse.js';
import store from '@/store';

import jsonFilters from '@/configs/filters.json';

import { separateContentTypesWithAndWithoutSession } from '@/helpers/dataManager';
import { isObjectEmpty } from './utils';

const searchLocalCustomers = (query, customers) => {
  const options = {
    useExtendedSearch: true, // White spaces act as a AND operator, this is usefull to search fullnames in multiple orders. Example: "João Pedro" becomes "João" AND "Pedro".
    includeScore: true,
    ignoreDiacritics: true,
    threshold: 0.25, // Adjust the threshold to make the matching more or less strict
    keys: ['firstname', 'lastname', 'fullname']
  };

  const fuse = new Fuse((customers || []), options);

  return fuse.search(`"${query}"`);
};

const searchByQuery = (wtype, contentTypeArray = null) => {
  const contents = contentTypeArray || store.state[`${wtype}s`];
  const searchQuery = store.state.search;

  let searchBy = '';
  switch (wtype) {
    case 'instructor':
      searchBy = 'fullname';
      break;
    case 'customer': {
      searchBy = 'fullname';

      if (searchQuery) {
        const customers = contents.map(c => ({
          uuid: c.uuid,
          firstname: c.firstname,
          lastname: c.lastname,
          fullname: c.fullname
        }));

        const result = searchLocalCustomers(searchQuery, customers);

        return contents.filter(c => result.some(r => r?.item?.uuid === c.uuid));
      }

      break;
    }

    default:
      searchBy = 'wtitle';
  }

  return contents.filter(ct => !searchQuery || ct[searchBy].toLowerCase().includes(searchQuery.toLowerCase()));
};

const searchByFilters = (searchResults, filters) => {
  const { sideBarFilters } = store.getters;
  const { currentTab } = store.state;

  filters.forEach(filterSettings => {
    const selectedFilterValue = sideBarFilters[currentTab];

    if (filterSettings.viewType === 'Switch' && filterSettings.filterType !== 'sessionFieldBased') {
      searchResults = searchResults.filter(ct => !!ct[filterSettings.field] === !!selectedFilterValue[filterSettings.field]);
    }

    if (filterSettings.filterType === 'sessionFieldBased') {
      const sessionFilter = selectedFilterValue.session;

      // remove absence ones
      searchResults = searchResults.filter(ct => {
        const sessions = store.getters.dailySessionsByContentType(ct);
        // means we are at the start an filter was not selected yet
        // so we will remove this content type for showing
        if (sessionFilter?.type && !sessionFilter?.type?.length) {
          return !sessions.some(s => filterSettings.values.includes(s.type));
        }
        return true;
      });

      if (sessionFilter && !isObjectEmpty(sessionFilter) && sessionFilter[filterSettings.field].length) {
        searchResults = searchResults.filter(ct => {
          const sessions = store.getters.dailySessionsByContentType(ct);

          return sessions.some(s => sessionFilter[filterSettings.field].includes(s[filterSettings.field]));
        });
      }
    }

    if (filterSettings.viewType === 'SelectGroup' && selectedFilterValue && selectedFilterValue[filterSettings.field] && selectedFilterValue[filterSettings.field].length) {
      searchResults = searchResults.filter(ct => {
        let values = ct[filterSettings.field] || [];

        if (!Array.isArray(values)) {
          values = [ct[filterSettings.field]];
        }

        return selectedFilterValue[filterSettings.field].some(s => values.includes(s));
      });
    }
  });
  return searchResults;
};

const filterContentType = wtype => {
  let contentArray = null;
  if (wtype === 'customer') {
    const { with_session, without_session } = separateContentTypesWithAndWithoutSession('customer');

    contentArray = [...with_session, ...without_session];
  }
  const { currentTab } = store.state;

  let searchResults = searchByQuery(wtype, contentArray);
  const filtersParsed = jsonFilters[currentTab];

  if (filtersParsed && filtersParsed.length) {
    const canApllyFilter = searchResults.every(ct => {
      let toCompare = ct.wtype;
      if (ct.wtype === 'collaborator') {
        toCompare = 'instructor';
      }
      return toCompare === currentTab;
    });
    if (canApllyFilter) {
      if (searchResults.length) {
        searchResults = searchByFilters(searchResults, filtersParsed);
      }
    }
  }

  return searchResults;
};

const algoliaClientConfiguration = (aplicationId, apiKey, namespace) => {
  const algoliaClient = algoliasearch(
    aplicationId,
    apiKey,
    {
      // Caches responses from Algolia
      responsesCache: createInMemoryCache(),
      // Caches Promises with the same request payload
      requestsCache: createInMemoryCache({ serializable: false })
    }
  );

  const algoliaIndex = namespace;

  return { algoliaClient, algoliaIndex };
};

const searchCustomer = async (searchValue, filters = null, start = 0, limit = 10) => {
  const { workspace } = store.state;
  const { algoliaClient, algoliaIndex } = algoliaClientConfiguration(process.env.VUE_APP_ALGOLIA_APPLICATION_ID, process.env.VUE_APP_ALGOLIA_SEARCH_API_KEY, process.env.VUE_APP_ALGOLIA_NAMESPACE);

  const index = algoliaClient.initIndex(algoliaIndex);

  const parts = workspace.code.split('_test');

  return index.search(searchValue || '', { filters: filters || `customer_workspaces:${parts[0]} AND wtype: customer`, offset: start, length: limit });
};

const searchCustomerOnAlgolia = async (searchValue, filters = null) => {
  const { algoliaCustomers } = store.state;
  const { hits } = await searchCustomer(searchValue, filters);

  (hits || []).map(hit => {
    const alreadyExist = algoliaCustomers.find(c => c.uuid === hit.uuid);

    if (!alreadyExist) {
      store.commit('setAlgoliaCustomer', { ...hit, fullname: `${hit.firstname} ${hit.lastname}` });
    }
  });
};

export { searchByQuery, filterContentType, algoliaClientConfiguration, searchCustomerOnAlgolia, searchCustomer, searchLocalCustomers };
